import { PreviewCanvasScale } from "./shared/preview-canvas-scale";
import { InsetProgressBar } from "./shared/progress-segment";
import type { ProgressBarLayer } from "./shared/progress-segment";

const INTER_TABULAR = {
  fontFeatureSettings: '"ss03" 1, "ss01" 1, "zero" 1, "ss02" 1',
} as const;

/** 12px track — Figma 11767:77733 inner cap layers use 11.851px height. */
const TRACK_H = 12;
const CAP_H = 11.851;
const CAP_INSET = { insetLeft: 0.31, insetTop: 0.1, height: CAP_H } as const;
const FULL_INSET = { insetLeft: 0, insetTop: 0, height: TRACK_H } as const;

const SCENARIO_ROWS: {
  label: string;
  top: number;
  left: number;
  layers: ProgressBarLayer[];
}[] = [
  {
    label: "Weaker",
    top: 48,
    left: 23,
    layers: [
      { width: 375, fill: "#FFFFFF", ...FULL_INSET },
      { width: 156, fill: "#F9F9F8", ...CAP_INSET },
      { width: 300, fill: "#FFFFFF", ...FULL_INSET },
      { width: 240, fill: "#F9F9F8", ...CAP_INSET },
      { width: 58.477, fill: "#F76E6E", ...CAP_INSET },
    ],
  },
  {
    label: "Expected",
    top: 70,
    left: 14,
    layers: [
      { width: 375, fill: "#FFFFFF", ...FULL_INSET },
      { width: 156, fill: "#F9F9F8", ...CAP_INSET },
      { width: 300, fill: "#FFFFFF", ...FULL_INSET },
      { width: 240, fill: "#F9F9F8", ...CAP_INSET },
      { width: 110.539, fill: "#FFCDB5", ...CAP_INSET },
    ],
  },
  {
    label: "Stronger",
    top: 92,
    left: 18,
    layers: [
      {
        width: 375,
        fill: "#ACC7FF",
        ...FULL_INSET,
        shadow:
          "inset 0.86px 1.721px 10px 0px rgba(0, 0, 0, 0.1), inset 0.86px 1.721px 10px 0px rgba(255, 255, 255, 0.25)",
      },
      { width: 156, fill: "#ACC7FF", ...CAP_INSET },
      {
        width: 300,
        fill: "#ACC7FF",
        ...FULL_INSET,
        shadow:
          "inset 0.86px 1.721px 10px 0px rgba(0, 0, 0, 0.1), inset 0.86px 1.721px 10px 0px rgba(255, 255, 255, 0.25)",
      },
      { width: 240, fill: "#ACC7FF", ...CAP_INSET },
      { width: 173.836, fill: "#ABF0B7", ...CAP_INSET },
    ],
  },
];

const LEGEND = [
  { color: "#ABF0B7", label: "Sold" },
  { color: "#FFCDB5", label: "Leftover" },
  { color: "#F76E6E", label: "Short" },
  { color: "#ACC7FF", label: "Your order" },
] as const;

/**
 * Order quantity card inset (Figma 11115:59240 desktop, 11128:40619 mobile).
 * Segmented inset progress bars — not the live tool SVG chart.
 */
export function OrderQuantityPreview() {
  return (
    <PreviewCanvasScale testId="order-quantity-canvas">
      <p
        className="text-grey-950 absolute top-[22px] left-[23px] text-[12px] leading-[1.4] whitespace-nowrap"
        style={INTER_TABULAR}
      >
        Consider ordering <span className="font-semibold">962 units</span>
      </p>

      <div className="border-grey-100 absolute top-[59px] left-[23px] h-[164px] w-[441px] overflow-hidden rounded-[10px] border bg-white">
        <div className="absolute top-[16px] left-[15px] flex items-center gap-2">
          {LEGEND.map(({ color, label }) => (
            <span
              key={label}
              className="text-grey-500 inline-flex shrink-0 items-center gap-1 text-[10px] leading-[1.4] whitespace-nowrap"
              style={INTER_TABULAR}
            >
              <span
                aria-hidden
                className="inline-block size-[10px] shrink-0 rounded-full shadow-[inset_0.86px_1.721px_10px_0px_rgba(255,255,255,0.25)]"
                style={{ backgroundColor: color }}
              />
              {label}
            </span>
          ))}
        </div>

        {SCENARIO_ROWS.map(({ label, top, left, layers }) => (
          <div
            key={label}
            className="absolute flex items-center gap-[10px]"
            style={{ top, left }}
          >
            <p
              className="text-grey-500 shrink-0 text-right text-[10px] leading-[1.4] whitespace-nowrap"
              style={INTER_TABULAR}
            >
              {label}
            </p>
            <InsetProgressBar
              trackHeight={TRACK_H}
              layers={layers}
              className="shrink-0"
            />
          </div>
        ))}
      </div>
    </PreviewCanvasScale>
  );
}
